import { cx } from "@suse-o11y/class-variance";
import { HourMinutes } from "@suse-o11y/date-time/format/clock-time-format";
import { formatAbsoluteTime } from "@suse-o11y/date-time/format/format-absolute-time";
import type { Time } from "@suse-o11y/date-time/time";
import { IconDelete, IconPencil } from "@suse-o11y/icons";
import React, { useRef, useState } from "react";

import { getBorgStatusConfig } from "../borg-status-config";
import type { BorgConversationListItem, BorgStatus } from "../models/borg-types";

const StatusBadge: React.FC<{ status: BorgStatus; className?: string }> = ({ status, className }) => {
	const config = getBorgStatusConfig(status);
	return (
		<span className={cx("shrink-0 rounded-full px-2 py-0.5 text-xs", config.className, className)}>{config.label}</span>
	);
};

const formatDate = (time: Time): string => formatAbsoluteTime(time, { clockTimeFormat: HourMinutes }).toString();

const InlineEdit: React.FC<{
	value: string;
	onSave: (value: string) => void;
	onCancel: () => void;
}> = ({ value, onSave, onCancel }) => {
	const [editValue, setEditValue] = useState(value);
	const cancelledRef = useRef(false);

	return (
		<input
			aria-label="Edit name"
			// biome-ignore lint/a11y/noAutofocus: inline edit should auto-focus
			autoFocus
			className="w-full rounded border border-primary-500 bg-white px-1.5 py-0.5 text-grey-900 text-sm outline-none"
			onBlur={() => {
				if (cancelledRef.current) {
					return;
				}
				const trimmed = editValue.trim();
				if (trimmed && trimmed !== value) {
					onSave(trimmed);
				} else {
					onCancel();
				}
			}}
			onChange={(e) => setEditValue(e.target.value)}
			onClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.currentTarget.blur();
				} else if (e.key === "Escape") {
					cancelledRef.current = true;
					onCancel();
				}
			}}
			value={editValue}
		/>
	);
};

interface HistoryItemProps {
	id: string;
	displayText: string;
	status: BorgStatus;
	startedAt: Time;
	isActive: boolean;
	onSelect: (id: string) => void;
	onDelete: (id: string) => void;
	onRename: (id: string, name: string) => void;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
	id,
	displayText,
	status,
	startedAt,
	isActive,
	onSelect,
	onDelete,
	onRename,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const canModify = status !== "in_progress";

	return (
		<div
			className={cx(
				"group flex items-start rounded-lg border px-3 py-2.5 transition-colors",
				isActive ? "border-primary-500 bg-primary-100/60" : "border-transparent hover:bg-grey-100",
			)}
		>
			{isEditing ? (
				<div className="min-w-0 flex-1">
					<InlineEdit
						onCancel={() => setIsEditing(false)}
						onSave={(name) => {
							onRename(id, name);
							setIsEditing(false);
						}}
						value={displayText}
					/>
					<p className="mt-1 text-grey-500 text-xs">{formatDate(startedAt)}</p>
				</div>
			) : (
				<button className="min-w-0 flex-1 cursor-pointer text-left" onClick={() => onSelect(id)} type="button">
					<div className="min-w-0 flex-1">
						<p className="line-clamp-2 text-grey-900 text-sm">{displayText}</p>
						<p className="mt-1 text-grey-500 text-xs">{formatDate(startedAt)}</p>
					</div>
				</button>
			)}
			{canModify && !isEditing ? (
				<>
					<StatusBadge className="group-hover:hidden" status={status} />
					<div className="hidden shrink-0 gap-0.5 group-hover:flex">
						<button
							aria-label="Rename"
							className="flex size-6 cursor-pointer items-center justify-center rounded text-grey-400 hover:bg-grey-200 hover:text-grey-600"
							onClick={(e) => {
								e.stopPropagation();
								setIsEditing(true);
							}}
							type="button"
						>
							<IconPencil className="size-3" />
						</button>
						<button
							aria-label="Delete"
							className="flex size-6 cursor-pointer items-center justify-center rounded text-grey-400 hover:bg-red-100 hover:text-red-600"
							onClick={(e) => {
								e.stopPropagation();
								onDelete(id);
							}}
							type="button"
						>
							<IconDelete className="size-3" />
						</button>
					</div>
				</>
			) : (
				<StatusBadge status={status} />
			)}
		</div>
	);
};

export const ConversationItem: React.FC<{
	item: BorgConversationListItem;
	isActive: boolean;
	onSelect: (id: string) => void;
	onDelete: (id: string) => void;
	onRename: (id: string, name: string) => void;
}> = ({ item, ...rest }) => (
	<HistoryItem
		displayText={item.display_name ?? item.initial_message}
		id={item.id}
		startedAt={item.started_at}
		status={item.status}
		{...rest}
	/>
);
